import React, { useState } from 'react';
import { useFormikContext } from 'formik';
import { disableExcludeDriverStates } from '@ourbranch/lookups';
import { Grid } from '@material-ui/core';
import PropTypes from 'prop-types';

import useSession from 'core/hooks/use-session';
import { useRouteHelper } from 'core/hooks/use-route-helper';
import SwapButton from 'core/components/swap-button';
import { Tooltip } from 'core/components/tooltip';
import { FormField } from 'core/components/form';
import RemoveLockedButton from 'core/components/remove-locked-button';
import RemoveDriverModal from './remove-driver-modal';
import useStyles from './driver-actions.styles';

const DriverActions = ({
  driver,
  formikFieldPath,
  onRemove,
  removeFromList,
  index,
  allowLicensedActions,
  state,
  hasPermissionToExcludeDriver,
  overrideDriverExclusionRules
}) => {
  const classes = useStyles();
  const { values, setFieldValue } = useFormikContext();
  const { canRemoveExcludedDriver, user, canSwapApplicants, isAgency } = useSession();
  const [showRemoveDriverModal, setShowRemoveDriverModal] = useState(false);
  const { inPolicyRoute, inOfferEditRoute } = useRouteHelper();
  const showSwapButton =
    canSwapApplicants && driver.isPrimary && values.drivers?.some((driver) => driver.isCoApplicant);
  const coApplicant = values.drivers?.find((person) => person.isCoApplicant);

  // never showing remove button at all for the primary driver because should not be able to remove the primary applicant
  const showRemoveLockedButton = !driver.isPrimary && inPolicyRoute && isAgency;
  const showRemoveEditButton = !driver.isPrimary && ((inPolicyRoute && !isAgency) || inOfferEditRoute);
  const disableCoApplicantField = (coApplicant && !driver.isCoApplicant) || driver.excludeDriver;
  const disableExcludeDriverField =
    !hasPermissionToExcludeDriver ||
    driver.isCoApplicant ||
    driver.isPrimary ||
    (disableExcludeDriverStates[state] && !overrideDriverExclusionRules);

  const fieldMode = driver.excludeDriver ? 'dark' : 'light';

  const excludedDriverTooltip = `${
    disableExcludeDriverStates[state] && !overrideDriverExclusionRules
      ? `${state} does not allow changing excluded driver status. `
      : ''
  }An excluded driver has no coverage under this policy.`;

  return (
    <Grid container justifyContent="flex-end" alignItems="center" spacing={4}>
      {showSwapButton && <SwapButton mode="big" allowLicensedActions={allowLicensedActions} formikPath="drivers" />}

      {showRemoveDriverModal && (
        <RemoveDriverModal
          open={showRemoveDriverModal}
          onClose={() => setShowRemoveDriverModal(false)}
          onRemove={() => {
            onRemove(index);
            removeFromList();
            setShowRemoveDriverModal(false);
          }}
          driver={driver}
          formikFieldPath={formikFieldPath}
        />
      )}
      {!driver.isPrimary && (
        <Grid item>
          <FormField
            id={`${formikFieldPath}.isCoApplicant`}
            name={`${formikFieldPath}.isCoApplicant`}
            type="checkbox"
            label="Co Applicant?"
            mode={fieldMode}
            disabled={disableCoApplicantField}
          />
        </Grid>
      )}
      {!driver.isPrimary && (
        <Grid item>
          <FormField
            id={`${formikFieldPath}.excludeDriver`}
            name={`${formikFieldPath}.excludeDriver`}
            type="checkbox"
            label={
              <div className={classes.excluded}>
                Exclude Driver
                <Tooltip text={excludedDriverTooltip} placement="bottom" label="More Info" />
              </div>
            }
            onChange={(_, checked) => {
              setFieldValue(
                `${formikFieldPath}.exclusionReason`,
                checked ? `Manually excluded by ${user.username}` : null
              );
            }}
            mode={fieldMode}
            disabled={disableExcludeDriverField}
          />
        </Grid>
      )}
      {showRemoveLockedButton && (
        <Grid item>
          <RemoveLockedButton mode="big" text="Please contact agency support to remove this driver." />
        </Grid>
      )}
      {showRemoveEditButton && (
        <Grid item>
          <FormField
            type="removeEdit"
            id={formikFieldPath}
            name={formikFieldPath}
            disabled={driver.isPrimary || (driver.excludeDriver && !canRemoveExcludedDriver)}
            mode="big"
            onClick={() => setShowRemoveDriverModal(true)}
            fast={false}
          />
        </Grid>
      )}
    </Grid>
  );
};

DriverActions.propTypes = {
  driver: PropTypes.object.isRequired,
  formikFieldPath: PropTypes.string.isRequired,
  onRemove: PropTypes.func.isRequired,
  removeFromList: PropTypes.func.isRequired,
  index: PropTypes.number.isRequired,
  allowLicensedActions: PropTypes.bool.isRequired,
  state: PropTypes.string.isRequired,
  hasPermissionToExcludeDriver: PropTypes.bool.isRequired
};

export default DriverActions;
